import React, { useState, useEffect } from "react";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import { FiArrowLeft, FiCopy, FiCheck } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import Toast from "../../components/Toast";
import apiService from "../../actions/apiService";
import { useAuth } from "../../context/authContext";

const PaymentInstructionsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const itemId = id || "";
  const [copiedField, setCopiedField] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeDeliveryFee, setIncludeDeliveryFee] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(500); // Default fallback
  const [acceptedOffer, setAcceptedOffer] = useState(null);

  // Fetch item details, platform config, and accepted offer
  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      if (!itemId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch item details
        const regularResult = await apiService.get(
          `/items/buyer/items/${itemId}`
        );
        if (!active) return;
        if (regularResult.success) {
          setItem(regularResult.data);
          // If no accepted offer exists (invite acceptance flow), use transaction flag to prefill authentication option
          const transactions = regularResult.data?.item?.transactions || [];
          const relevantTx =
            transactions.find(
              (tx) => tx?.status === "pending" || tx?.status === "accepted"
            ) ||
            transactions[0] ||
            null;
          if (relevantTx?.is_delivered_door !== undefined) {
            setIncludeDeliveryFee(Boolean(relevantTx.is_delivered_door));
          }
        }

        // Fetch accepted offer for this item
        try {
          const offerHistoryResult = await apiService.get(
            `/requests/item/${itemId}/history`
          );
          if (!active) return;
          if (offerHistoryResult.success && offerHistoryResult.data?.requests) {
            // Find accepted offer
            const accepted = offerHistoryResult.data.requests.find(
              (req) => req.status === "accepted"
            );
            if (accepted) {
              setAcceptedOffer(accepted);
              // Set delivery fee preference from accepted offer if available
              // This preference was set when the offer was created
              if (accepted.is_delivered_door !== undefined) {
                setIncludeDeliveryFee(accepted.is_delivered_door);
              }
            }
          }
        } catch (offerError) {
          console.warn("Failed to fetch offer history:", offerError);
        }

        // Fetch delivery fee
        try {
          const configResult = await apiService.get(
            "/transactions/delivery-fee"
          );
          if (!active) return;
          if (configResult.success && configResult.data?.delivery_fee) {
            setDeliveryFee(Number(configResult.data.delivery_fee));
          }
        } catch (configError) {
          // If config fetch fails, use default value (already set to 500)
          console.warn(
            "Failed to fetch delivery fee, using default:",
            configError
          );
        }
      } catch (error) {
        console.error("Failed to fetch item details:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [itemId]);

  console.log("item", item);

  // Calculate total amount - use accepted offer if available, otherwise use original item price
  let baseAmount = 0;
  if (acceptedOffer) {
    // Calculate from accepted offer: item price + service fee
    const offerAmount = Number(acceptedOffer.offer_amount) || 0;
    const serviceFeePercentage = item?.pricing?.buyerFeePercentage || 0;
    const itemPrice = offerAmount;
    const serviceFee = itemPrice * (serviceFeePercentage / 100);
    baseAmount = itemPrice + serviceFee; // Total from accepted offer
  } else {
    // Use original item pricing
    baseAmount = item?.pricing?.finalPrice || 0;
  }

  // Use is_delivered_door from accepted offer if available, otherwise use current state
  const deliveryFeeAmount =
    item?.item?.delivered_to_door &&
    (acceptedOffer?.is_delivered_door || includeDeliveryFee)
      ? deliveryFee
      : 0;
  const totalAmount = Number(baseAmount) + Number(deliveryFeeAmount);

  // Bank details - Escrow company account
  const bankDetails = {
    amount: totalAmount,
    accountNumber: "001024023",
    swift: "FBAO08",
    referenceCode: item?.item?.reference_code,
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setToastMessage(`${fieldName} copied to clipboard`);
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  const handleInitiatePayment = async () => {
    if (!itemId) {
      setPaymentError("Item ID is required");
      return;
    }
    setPaymentError("");
    setSubmittingPayment(true);
    // Use is_delivered_door from accepted offer if available, otherwise use current state
    const finalIsDeliveredDoor =
      acceptedOffer?.is_delivered_door !== undefined
        ? acceptedOffer.is_delivered_door
        : includeDeliveryFee && item?.item?.delivered_to_door;
    const payload = {
      item_id: itemId,
      notes: "transaction initiated by buyer",
      is_delivered_door: finalIsDeliveredDoor,
    };
    const res = await apiService.post("/transactions/initiate", payload);
    if (!res.success) {
      const errorMessage = res.error || "Failed to initiate payment";
      setPaymentError(errorMessage);
      setSubmittingPayment(false);

      // Check if transaction already exists
      if (
        errorMessage.toLowerCase().includes("transaction already exists") ||
        errorMessage.toLowerCase().includes("already exists for this item")
      ) {
        // Show error message briefly, then redirect to transactions page
        setTimeout(() => {
          navigate("/buyer/transactions");
        }, 3000);
      }
    } else {
      setSubmittingPayment(false);
      setToastMessage("Payment initiated successfully!");
      // After successful initiation, proceed to confirm transfer
      handleConfirmTransfer();
    }
  };

  const handleConfirmTransfer = async () => {
    setConfirming(true);
    setPaymentError("");

    try {
      // Get transaction ID from item data (if available) or fetch it
      let transactionId = null;

      // Check if item has transactions
      if (item?.item?.transactions && item.item.transactions.length > 0) {
        const transaction = item.item.transactions.find(
          (tx) => tx.status === "pending" || tx.status === "accepted"
        );
        if (transaction) {
          transactionId = transaction.id;
        }
      }

      // If not found in item data, fetch it
      if (!transactionId) {
        const transactionRes = await apiService.get(
          `/transactions/by-user/list?item_id=${itemId}${
            user?.id ? `&buyer_id=${user.id}` : ""
          }`
        );

        if (
          transactionRes.success &&
          transactionRes.data?.transactions?.length > 0
        ) {
          const transaction = transactionRes.data.transactions.find(
            (tx) =>
              tx.item_id === itemId &&
              (tx.status === "pending" || tx.status === "accepted")
          );
          if (transaction) {
            transactionId = transaction.id;
          }
        }
      }

      if (transactionId) {
        // Mark payment as initiated
        const initiateRes = await apiService.post(
          `/transactions/${transactionId}/initiate-payment`,
          {
            buyer_id: user?.id,
            seller_id: item?.item?.seller_id,
          }
        );

        console.log("initiateRes", initiateRes);

        if (initiateRes.success) {
          setConfirming(false);
          setToastMessage(
            "Payment initiated successfully! Awaiting verification."
          );
          // Redirect to transactions page after confirmation
          setTimeout(() => {
            navigate("/buyer/transactions");
          }, 2000);
        } else {
          setConfirming(false);
          setPaymentError(initiateRes.error || "Failed to initiate payment");
        }
      } else {
        setConfirming(false);
        setPaymentError("Failed to initiate payment. Please try again. 1");
      }
    } catch (error) {
      setConfirming(false);
      setPaymentError(
        error.message || "Failed to initiate payment. Please try again."
      );
      console.error("Error initiating payment:", error);
    }
  };

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10] px-5 lg:px-0">
        <div className="text-[14px] flex items-center justify-start text-white lg:text-esblue font-[600] mb-8">
          <Link
            className="flex items-center justify-center gap-1"
            to={"/buyer/transactions"}
          >
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to transactions
          </Link>
        </div>

        <h1 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik mb-8">
          Bank Transfer Payment
        </h1>

        <div className="w-full bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-[25px] lg:rounded-[12px] p-5">
          <div className="mb-8">
            <h2 className="text-[20px] md:text-[24px] font-[600] text-[#1E1E1E] mb-6">
              Bank Details
            </h2>

            <div className="space-y-8">
              {/* Amount */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-[12px] text-gray-500 mb-1">
                    Amount to send
                  </p>
                  <div>
                    <p className="text-[18px] font-[600] text-[#1E1E1E]">
                      {loading
                        ? "Loading..."
                        : bankDetails.amount
                        ? `${Number(bankDetails.amount).toLocaleString()}€`
                        : "N/A"}
                    </p>
                    {(acceptedOffer?.is_delivered_door || includeDeliveryFee) &&
                      item?.item?.delivered_to_door && (
                        <div className="mt-1 text-[12px] text-gray-500">
                          <span>
                            {Number(baseAmount).toLocaleString()}€ (Item) +{" "}
                            {deliveryFee.toLocaleString()}€ (Authentication
                            check delivery fee included)
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Account Number */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-[12px] text-gray-500 mb-1">
                    Account number
                  </p>
                  <p className="text-[18px] font-[600] text-[#1E1E1E]">
                    {bankDetails.accountNumber}
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleCopy(bankDetails.accountNumber, "Account number")
                  }
                  className="mt-2 md:mt-0 md:ml-4 p-2 text-gray-600 hover:text-esblue transition-colors"
                >
                  {copiedField === "Account number" ? (
                    <FiCheck className="w-5 h-5 text-green-600" />
                  ) : (
                    <FiCopy className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* SWIFT */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-[12px] text-gray-500 mb-1">SWIFT</p>
                  <p className="text-[18px] font-[600] text-[#1E1E1E]">
                    {bankDetails.swift}
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(bankDetails.swift, "SWIFT")}
                  className="mt-2 md:mt-0 md:ml-4 p-2 text-gray-600 hover:text-esblue transition-colors"
                >
                  {copiedField === "SWIFT" ? (
                    <FiCheck className="w-5 h-5 text-green-600" />
                  ) : (
                    <FiCopy className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Reference Code - Highlighted */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-blue-50 rounded-lg border-2 border-esblue">
                <div className="flex-1">
                  <p className="text-[12px] text-esblue mb-1 font-[600]">
                    Reference Code to include in the transfer
                  </p>
                  <p className="text-[20px] font-[700] text-esblue">
                    {loading || !bankDetails.referenceCode ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-esblue"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Loading...
                      </span>
                    ) : (
                      bankDetails.referenceCode
                    )}
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleCopy(bankDetails.referenceCode, "Reference code")
                  }
                  disabled={loading || !bankDetails.referenceCode}
                  className="mt-2 md:mt-0 md:ml-4 p-2 text-esblue hover:bg-esblue hover:text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copiedField === "Reference code" ? (
                    <FiCheck className="w-5 h-5" />
                  ) : (
                    <FiCopy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Confirm Button */}
          <div className="w-full flex flex-col items-end justify-end gap-4">
            {paymentError && (
              <div className="text-red-500 text-[13px]">{paymentError}</div>
            )}
            <button
              onClick={handleInitiatePayment}
              disabled={confirming || submittingPayment}
              className="w-full md:w-auto px-8 py-3 bg-blue-gradient text-white rounded-lg font-[600] text-[16px]  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingPayment
                ? "Initiating payment..."
                : confirming
                ? "Confirming..."
                : "I made the transfer"}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage("")}
          type="success"
        />
      )}
    </LayoutBuyerSeller>
  );
};

export default PaymentInstructionsPage;
